/**
 * Object → print binding contract (ADR 0045).
 *
 * The engine's `metadata` is opaque; the label domain stores its per-object
 * print semantics under ONE key — `metadata.binding` — validated by
 * `getBinding` so a hand-edited/legacy object degrades to null instead of
 * crashing a drawer. Text content with `{{tokens}}` lives in `style.text`
 * (the engine precedent); binding carries everything else.
 *
 * Compiler-excluded (`services/`) — callable from drawers, press handlers,
 * and the print pipeline.
 *
 * @module services/labelTemplates/binding
 * @category Services
 */

import type { LabelTemplateDocument } from '@fuze-store/shared';

import type {
  Barcode1dSymbology,
  LabelVariableContext,
} from '@fuze-store/print-engine/label';
import { interpolateTokens } from '@fuze-store/print-engine/label';
import type { FloorObject } from '@src/floorplan/models/object';

type LabelTextOverflow = 'shrink' | 'truncate';
type LabelTextAlign = 'left' | 'center' | 'right';
type LabelQrEcc = 'L' | 'M' | 'Q' | 'H';

export type LabelObjectBinding =
  | {
      kind: 'labelText';
      overflow: LabelTextOverflow;
      align: LabelTextAlign;
      bold: boolean;
    }
  | {
      kind: 'barcode';
      /** Literal data OR a `{{token}}` expression. */
      data: string;
      symbology: Barcode1dSymbology;
      hri: 'none' | 'below';
    }
  | {
      kind: 'qr';
      data: string;
      ecc: LabelQrEcc;
    };

/** Object types the label designer registers. */
export const LABEL_OBJECT_TYPE = {
  TEXT: 'labelText',
  BARCODE: 'barcode',
  QR: 'qr',
} as const;

const TEXT_OVERFLOWS: readonly string[] = ['shrink', 'truncate'];
const TEXT_ALIGNS: readonly string[] = ['left', 'center', 'right'];
const SYMBOLOGIES: readonly string[] = ['CODE128', 'EAN13', 'UPC_A', 'CODE39'];
const ECCS: readonly string[] = ['L', 'M', 'Q', 'H'];

/** Read + validate the binding off an object. Null when absent/malformed. */
export const getBinding = (object: FloorObject): LabelObjectBinding | null => {
  const raw = object.metadata['binding'];
  if (raw === null || typeof raw !== 'object') return null;
  const b = raw as Record<string, unknown>;
  if (b['kind'] === 'labelText') {
    return {
      kind: 'labelText',
      overflow: TEXT_OVERFLOWS.includes(String(b['overflow']))
        ? (b['overflow'] as LabelTextOverflow)
        : 'shrink',
      align: TEXT_ALIGNS.includes(String(b['align']))
        ? (b['align'] as LabelTextAlign)
        : 'center',
      bold: b['bold'] === true,
    };
  }
  if (b['kind'] === 'barcode') {
    return {
      kind: 'barcode',
      data: typeof b['data'] === 'string' ? b['data'] : '',
      symbology: SYMBOLOGIES.includes(String(b['symbology']))
        ? (b['symbology'] as Barcode1dSymbology)
        : 'CODE128',
      hri: b['hri'] === 'none' ? 'none' : 'below',
    };
  }
  if (b['kind'] === 'qr') {
    return {
      kind: 'qr',
      data: typeof b['data'] === 'string' ? b['data'] : '',
      ecc: ECCS.includes(String(b['ecc'])) ? (b['ecc'] as LabelQrEcc) : 'M',
    };
  }
  return null;
};

/**
 * Build a `makeTransformCommand`-shaped patch that writes the binding —
 * merging over the object's other metadata keys.
 */
export const setBindingPatch = (
  object: FloorObject,
  binding: LabelObjectBinding,
): Partial<FloorObject> => ({
  metadata: { ...object.metadata, binding },
});

/**
 * Resolve every `{{token}}` in the document against a variable context —
 * `style.text` on text objects, `binding.data` on barcode/qr objects.
 * Returns a DEEP COPY; resolution happens at print/preview time only and is
 * never persisted.
 */
export const resolveLabelDocument = (
  doc: LabelTemplateDocument,
  ctx: LabelVariableContext,
): LabelTemplateDocument => {
  const objects = (doc.engineDoc.objects as FloorObject[]).map((object) => {
    const next: FloorObject = {
      ...object,
      style: object.style ? { ...object.style } : undefined,
      metadata: { ...object.metadata },
    };
    if (next.style?.text !== undefined) {
      next.style.text = interpolateTokens(next.style.text, ctx);
    }
    const binding = getBinding(next);
    if (binding !== null && binding.kind !== 'labelText') {
      next.metadata = {
        ...next.metadata,
        binding: { ...binding, data: interpolateTokens(binding.data, ctx) },
      };
    }
    return next;
  });
  return {
    ...doc,
    engineDoc: { ...doc.engineDoc, objects },
  };
};
